import { DEDUPING_INTERVAL, POLL_INTERVAL } from "@/constants";
import type { WebUserRoundEntryUnitWithResults } from "@/types/WebUserRoundEntryUnit";
import { isUserLoggedIn } from "@/utils";
import { authHeader } from "@roman/shared/lib/auth/authHeader";
import romanApi from "@roman/shared/lib/net/romanApi";
import type { Language, LanguageQuery } from "@roman/shared/types/Language";
import useSWR, { type SWRConfiguration } from "swr";

export type Status = "schedule" | "live" | "prizeDistributed" | "cancelled";

export interface Props {
  status: Status;
  lang: Language;
  swrOptions?: SWRConfiguration;
  fetcher?: (url: string, status: Status, lang: Language) => Promise<UserRoundEntryUnitsResponse>;
}

export interface UserRoundEntryUnitsResponse {
  userRoundEntryUnits: WebUserRoundEntryUnitWithResults[];
}

// This factory pattern for better testability
export const createDefaultFetcher = () => {
  return (url: string, status: Status, lang: Language) => {
    const headers = authHeader.getOrThrow();
    return romanApi.get<UserRoundEntryUnitsResponse, LanguageQuery & { status: Status }>(
      url,
      { status, lang },
      { headers },
    );
  };
};

export const useWebUserRoundEntryUnits = ({ status, lang, swrOptions, fetcher }: Props) => {
  const authenticated = isUserLoggedIn();
  const roundEntryUnitsFetcher = fetcher || createDefaultFetcher();

  const options: SWRConfiguration = {
    refreshInterval: POLL_INTERVAL,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: DEDUPING_INTERVAL,
    ...swrOptions,
  };

  const fetcherKey = authenticated ? ["/web/soccer/roundEntryUnits", status, lang] : null;

  const swrQuery = useSWR(
    fetcherKey,
    ([url, status, lang]: [string, Status, Language]) => roundEntryUnitsFetcher(url, status, lang),
    options,
  );

  const userRoundEntryUnits = swrQuery.data?.userRoundEntryUnits ?? [];

  return {
    userRoundEntryUnits,
    ...swrQuery,
  };
};
